import { FlowDetailSuppliers } from 'hooks/data/datasets/V4/flows/types';

import { LAYOUT_CONFIG } from './constants';
import type { SourceNode } from './types';

// Flow colors - consistent in both light and dark mode
const FLOW_COLORS = {
  transformingActivity: { flow: '#68D391', node: '#38A169' },
  importMix: { flow: '#4FD1C5', node: '#319795' },
  domesticSupplyMix: { flow: '#63B3ED', node: '#3182CE' },
  consumptionMix: { flow: '#7DD3FC', node: '#0891B2' },
  globalSupplyMix: { flow: '#A78BFA', node: '#7C3AED' },
  default: { flow: '#60A5FA', node: '#3B82F6' },
} as const;

const COLOR_MAP: Record<FlowDetailSuppliers['type'], { flow: string; node: string }> = {
  'Transforming activity': FLOW_COLORS.transformingActivity,
  'Import mix': FLOW_COLORS.importMix,
  'Domestic supply mix': FLOW_COLORS.domesticSupplyMix,
  'Consumption mix': FLOW_COLORS.consumptionMix,
  'Global supply mix': FLOW_COLORS.globalSupplyMix,
};

/**
 * Get the flow or node color for a given supplier type
 * @param type - The supplier type
 * @param variant - Whether to get 'flow' or 'node' color
 * @returns The color hex string
 */
export const getSupplierColor = (type: FlowDetailSuppliers['type'], variant: 'flow' | 'node'): string => {
  return COLOR_MAP[type]?.[variant] || FLOW_COLORS.default[variant];
};

/**
 * Get the most common unit from sources with stable alphabetical tiebreaker
 */
export const getMostCommonUnit = (sources: FlowDetailSuppliers[]): string => {
  if (sources.length === 0) return 'units';

  const unitCounts = sources.reduce(
    (acc, source) => {
      acc[source.unit] = (acc[source.unit] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>
  );

  const entries = Object.entries(unitCounts);
  if (entries.length === 0) {
    return 'units';
  }
  // Sort with stable tiebreaker: count descending, then alphabetical ascending
  entries.sort((a, b) => {
    if (b[1] !== a[1]) {
      return b[1] - a[1];
    }
    return a[0].localeCompare(b[0]);
  });
  return entries[0][0];
};

/**
 * Calculate adaptive layout configuration based on source count
 * Returns spacing, node height multiplier, and minimum height based on density tiers
 */
export const getAdaptiveLayoutConfig = (count: number, baseHeight: number) => {
  if (count > LAYOUT_CONFIG.thresholds.veryHigh) {
    return {
      spacing: LAYOUT_CONFIG.spacing.high,
      nodeHeight: baseHeight * LAYOUT_CONFIG.heightMultipliers.veryHigh,
      minHeight: LAYOUT_CONFIG.minHeights.veryHigh,
    };
  }
  if (count > LAYOUT_CONFIG.thresholds.high) {
    return {
      spacing: LAYOUT_CONFIG.spacing.medium,
      nodeHeight: baseHeight * LAYOUT_CONFIG.heightMultipliers.high,
      minHeight: LAYOUT_CONFIG.minHeights.high,
    };
  }
  if (count > LAYOUT_CONFIG.thresholds.low) {
    return {
      spacing: LAYOUT_CONFIG.spacing.low,
      nodeHeight: baseHeight * LAYOUT_CONFIG.heightMultipliers.medium,
      minHeight: LAYOUT_CONFIG.minHeights.medium,
    };
  }
  return {
    spacing: LAYOUT_CONFIG.spacing.low,
    nodeHeight: baseHeight * LAYOUT_CONFIG.heightMultipliers.low,
    minHeight: LAYOUT_CONFIG.minHeights.low,
  };
};

/**
 * Helper function to truncate text based on source count
 */
export const getTruncatedText = (text: string, sourceCount: number): string => {
  const maxLength =
    sourceCount > LAYOUT_CONFIG.thresholds.medium
      ? LAYOUT_CONFIG.labelCard.truncationLarge
      : LAYOUT_CONFIG.labelCard.truncationSmall;
  return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
};

/**
 * Helper function to calculate and validate percentage
 */
export const calculatePercentage = (amount: number, total: number): number => {
  // Guard against invalid or zero totals to avoid division by zero/NaN
  if (total <= 0) {
    return 0;
  }
  // Normalize amount to be non-negative before computing the percentage
  const safeAmount = Math.max(0, amount);
  const percentage = (safeAmount / total) * 100;
  // Clamp to 0-100 range to handle any floating point edge cases
  return Math.max(0, Math.min(100, percentage));
};

/**
 * Generate SVG path for a gently curved flow from source to target
 * Uses subtle cubic Bezier curves to create smooth connections between nodes
 */
export const generatePath = (source: SourceNode, targetX: number, targetYStart: number, targetHeight: number) => {
  const sourceTop = source.y;
  const sourceBottom = source.y + source.height;
  const targetTop = targetYStart;
  const targetBottom = targetYStart + targetHeight;

  // Control points positioned closer to endpoints for a subtle curve
  const distance = targetX - (source.x + source.width);
  const controlOffset = distance * 0.3; // 30% of distance for subtle curve

  const sourceControl = source.x + source.width + controlOffset;
  const targetControl = targetX - controlOffset;

  return `M ${source.x + source.width} ${sourceTop}
          C ${sourceControl} ${sourceTop}, ${targetControl} ${targetTop}, ${targetX} ${targetTop}
          L ${targetX} ${targetBottom}
          C ${targetControl} ${targetBottom}, ${sourceControl} ${sourceBottom}, ${source.x + source.width} ${sourceBottom}
          Z`;
};

/**
 * Calculate source node positions using a cumulative layout algorithm
 */
export const calculateSourceNodes = (
  sortedSources: FlowDetailSuppliers[],
  totalAmount: number,
  getSourceColor: (type: FlowDetailSuppliers['type']) => string
): SourceNode[] => {
  const sourceCount = sortedSources.length;
  const adaptiveLayout = getAdaptiveLayoutConfig(sourceCount, LAYOUT_CONFIG.baseNodeHeight);
  const adaptiveSpacing = adaptiveLayout.spacing;
  const adaptiveNodeHeight = adaptiveLayout.nodeHeight;
  const adaptiveMinHeight = adaptiveLayout.minHeight;

  return sortedSources.map((source, index) => {
    const height = Math.max(adaptiveMinHeight, (source.amount / totalAmount) * adaptiveNodeHeight);
    const y =
      sortedSources
        .slice(0, index)
        .reduce((sum, s) => sum + Math.max(adaptiveMinHeight, (s.amount / totalAmount) * adaptiveNodeHeight), 0) +
      index * adaptiveSpacing;

    return {
      ...source,
      x: LAYOUT_CONFIG.sourceNodesX,
      y: y + LAYOUT_CONFIG.topOffset,
      width: LAYOUT_CONFIG.nodeWidth,
      height: height,
      color: getSourceColor(source.type),
    };
  });
};

/**
 * Calculate target node position
 * Note: Target node is only used for positioning flow endpoints (x coordinate)
 */
export const calculateTargetNode = (svgWidth: number) => {
  return {
    x: svgWidth - LAYOUT_CONFIG.rightMargin,
    y: LAYOUT_CONFIG.topOffset,
    width: LAYOUT_CONFIG.nodeWidth,
  };
};
